import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Breakpoint,
	Callout,
	FlexGap,
	Heading4,
	NumberedSubheading,
	Paragraph,
	QuillText,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import { StyrBadge } from '../../../connectors/products/StyrBadge';
import EvaluationSubmissionItemDevelopmentPlan from '../EvaluationSubmissionItemDevelopmentPlan';
import EvaluationSubmissionAnswer from './EvaluationSubmissionAnswer';
import EvaluationSubmissionReviewGoals from './EvaluationSubmissionReviewGoals';
import EvaluationSubmissionSetGoals from './EvaluationSubmissionSetGoals';
import { StreamingEvaluationAnswerAIInsights } from './StreamingEvaluationAnswerAIInsights';
import CompetencyDisplay from './display/CompetencyDisplay';
import EvaluationNotes from './notes/EvaluationNotes';

const Container = styled(Flex)`
	&:not(:last-child) {
		margin-bottom: ${tokens.gap.page}px;
	}
`;

const Body = styled(Box)`
	padding-left: ${26 + tokens.gap.group}px;

	@media screen and (max-width: ${Breakpoint.Small}) {
		padding-left: 0;
	}
`;

const Description = styled.div`
	font-size: ${tokens.typography.sizes.body}px;
	line-height: ${tokens.typography.lineHeights.body};
`;

const OptionalLabel = styled.span`
	margin-left: ${tokens.gap.element}px;
	color: ${tokens.text.tertiary};
	font-size: ${tokens.typography.sizes.small}px;
	font-weight: ${tokens.typography.weights.regular};
	line-height: ${tokens.typography.lineHeights.small};
`;

const EvaluationSubmissionBody = ({
	type,
	item,
	section,
	submission,
	fillInBy,
	onSave,
	isSaving,
}: {
	type: graphql.EvaluationSectionItemType;
	item: number;
	section: number;
	submission: graphql.EvaluationSubmission;
	fillInBy: {
		employee: boolean;
		manager: boolean;
	};
	isSaving: boolean;
	onSave: () => void;
}) => {
	const user = submission.employee.user;

	if (type === graphql.EvaluationSectionItemType.DevelopmentPlan) {
		if (submission.employee.user.viewerCanViewDevelopmentPlan) {
			return <EvaluationSubmissionItemDevelopmentPlan userID={user.id} />;
		}

		return (
			<Callout
				title={t('evaluation.submission.developmentPlan.notVisible.title')}
				description={t('evaluation.submission.developmentPlan.notVisible.description')}
				color={'blue'}
			/>
		);
	}

	if (type === graphql.EvaluationSectionItemType.ReviewGoals) {
		return (
			<EvaluationSubmissionReviewGoals
				section={section}
				item={item}
				submission={submission}
				fillInBy={fillInBy}
				onSave={onSave}
				isSaving={isSaving}
			/>
		);
	}

	if (type === graphql.EvaluationSectionItemType.SetGoals) {
		if (submission.employee.user.viewerCanViewGoals) {
			return <EvaluationSubmissionSetGoals userID={user.id} />;
		}

		return (
			<Callout
				title={t('evaluation.submission.setGoals.notVisible.title')}
				description={t('evaluation.submission.setGoals.notVisible.description')}
				color={'blue'}
			/>
		);
	}

	return (
		<>
			<EvaluationSubmissionAnswer
				party={graphql.EvaluationSubmissionParty.Employee}
				itemIndex={item}
				sectionIndex={section}
				submission={submission}
				onSave={onSave}
				isSaving={isSaving}
			/>

			<EvaluationSubmissionAnswer
				party={graphql.EvaluationSubmissionParty.Manager}
				itemIndex={item}
				sectionIndex={section}
				submission={submission}
				onSave={onSave}
				isSaving={isSaving}
			/>
		</>
	);
};

function EvaluationSubmissionTitle({
	item,
	index,
	showMissingRequiredFieldError,
}: {
	item: graphql.EvaluationSectionItem;
	index: number;
	showMissingRequiredFieldError?: boolean;
}) {
	const { t } = useTranslation();
	const { localize, language } = useLocalize();

	let title: graphql.LocalizedString | undefined = undefined;

	if (item.openQuestion) {
		title = item.openQuestion.title;
	}

	if (item.stars) {
		title = item.stars.title;
	}

	if (item.sentiment) {
		title = item.sentiment.title;
	}

	if (item.multipleChoice) {
		title = item.multipleChoice.title;
	}

	if (item.competency) {
		title = item.competency.competency.title;
	}

	if (item.reviewGoals) {
		title = [
			{
				languageID: language.id,
				text: t('evaluation.submission.reviewGoals.title'),
			},
		];
	}

	if (item.developmentPlan) {
		title = [
			{
				languageID: language.id,
				text: t('evaluation.submission.developmentPlan.title'),
			},
		];
	}

	if (item.setGoals) {
		title = item.setGoals.title;
	}

	return title ? (
		<NumberedSubheading index={index} showWarningBackground={showMissingRequiredFieldError}>
			<FlexGap alignItems="center" gap={tokens.gap.element} flexWrap="wrap">
				<Subheading>
					{localize(title).text}
					{item.optional && <OptionalLabel>*{t('common.optional')}</OptionalLabel>}
				</Subheading>
				{item.competency?.competency.styrSkillID && <StyrBadge />}
			</FlexGap>
		</NumberedSubheading>
	) : null;
}

export default function EvaluationSubmissionItem(props: {
	section: number;
	item: number;
	submission: graphql.EvaluationSubmission;
	onSave: () => void;
	isSaving: boolean;
	isLastItemOfSection: boolean;
	showMissingRequiredFieldError?: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const item = props.submission.evaluationForm.sections[props.section].items[props.item];

	const ignored = [
		graphql.EvaluationSectionItemType.Title,
		graphql.EvaluationSectionItemType.Paragraph,
		graphql.EvaluationSectionItemType.FinalScore,
		graphql.EvaluationSectionItemType.Signature,
	];

	// To determine the question number, we count all preceding questions that should display a number.
	const index =
		props.submission.evaluationForm.sections
			.flatMap((section, sectionIndex) =>
				section.items.map((item, itemIndex) => ({
					sectionIndex,
					itemIndex,
					type: item.type,
				})),
			)
			// Exclude ignored 'question types' (i.e. visual elements such as titles and paragraphs).
			.filter(({ type }) => !ignored.includes(type))
			// Limit to all preceding question.
			.filter(
				({ sectionIndex, itemIndex }) =>
					sectionIndex < props.section ||
					(sectionIndex === props.section && itemIndex < props.item),
			).length + 1;

	if (
		(item.title || item.paragraph) &&
		!(
			item.fillInBy === graphql.EvaluationFillInBy.Both ||
			item.fillInBy.valueOf() === props.submission.viewer.party.valueOf() ||
			props.submission.employee.hasShared ||
			props.submission.manager.hasShared
		)
	) {
		return null;
	}

	if (item.title) {
		const sectionTitle = localize(item.title.text).text;
		const formTitle = localize(props.submission.evaluationForm.title).text;

		if (!sectionTitle || sectionTitle === formTitle) {
			return null;
		}

		return (
			<Box mb={`${tokens.gap.section}px`}>
				<Heading4>{sectionTitle}</Heading4>
			</Box>
		);
	} else if (item.paragraph) {
		return (
			<Box mb={props.isLastItemOfSection ? 0 : 5}>
				<Description>
					<QuillText value={localize(item.paragraph.text).text} />
				</Description>
			</Box>
		);
	} else if (ignored.includes(item.type)) {
		return null;
	} else {
		const fillInBy = {
			employee: [graphql.EvaluationFillInBy.Employee, graphql.EvaluationFillInBy.Both].includes(
				item.fillInBy,
			),
			manager: [graphql.EvaluationFillInBy.Manager, graphql.EvaluationFillInBy.Both].includes(
				item.fillInBy,
			),
		};

		return (
			<Container flexWrap="wrap" alignItems="stretch" flexDirection={['column', 'column', 'row']}>
				<Box flex={1} flexGrow={1}>
					<EvaluationSubmissionTitle
						item={item}
						index={index}
						showMissingRequiredFieldError={props.showMissingRequiredFieldError}
					/>
					{props.showMissingRequiredFieldError && (
						<Callout color="yellow" mb={3}>
							{t('evaluation.submission.missing.required.field')}
						</Callout>
					)}
					{item.competency && (
						<Body mb={3}>
							<QuillText value={localize(item.competency.competency.description ?? [])?.text} />
						</Body>
					)}
					{item.setGoals && (
						<Body mb={3}>
							<Paragraph>{t('evaluation.submission.setGoals.description')}</Paragraph>
						</Body>
					)}
					{item.type === graphql.EvaluationSectionItemType.Competency && (
						<Body>
							<CompetencyDisplay
								party={'both'}
								submission={props.submission}
								section={props.section}
								item={props.item}
							/>
						</Body>
					)}
					{!(
						item.type === graphql.EvaluationSectionItemType.Competency &&
						!props.submission.viewer.viewerCanUpdate
					) && (
						<Body>
							<EvaluationSubmissionBody
								section={props.section}
								item={props.item}
								type={item.type}
								submission={props.submission}
								fillInBy={fillInBy}
								onSave={props.onSave}
								isSaving={props.isSaving}
							/>
						</Body>
					)}
					<Body>
						<StreamingEvaluationAnswerAIInsights
							id={item.id}
							autoOpen={!props.submission.viewer.hasShared}
							side={
								props.submission.viewer.party === graphql.EvaluationSubmissionParty.Manager
									? 'right'
									: 'left'
							}
						/>
					</Body>
				</Box>
				{item.type !== graphql.EvaluationSectionItemType.DevelopmentPlan && (
					<Box
						ml={[0, 0, 0, 5]}
						mt={[3, 3, 3, 0]}
						flex={[1, 1, 1, 5 / 12]}
						style={{
							minWidth: 200,
							maxWidth: 300,
						}}
					>
						{(props.submission.viewer.hasShared || !props.submission.viewer.viewerCanUpdate) && (
							<EvaluationNotes submission={props.submission} itemID={item.id} />
						)}
					</Box>
				)}
			</Container>
		);
	}
}
